/**
 * FriendCardAccepted component
 * Displays accepted friend card with avatar, name, and stats
 */
import React, { useMemo } from 'react'
import { GestureResponderEvent, View, Text } from 'react-native'
import { Image } from 'expo-image'
import { useRouter } from 'expo-router'
import { BaseCard } from '../BaseCard'
import { getSafeDisplayName } from '../../../src/create/helpers/createUi.helper'
import { MONKEY_USER_ID } from '../../../constants'
import { IDLE_COIN_SOURCE } from '../../../constants/assets'
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper'
import { useTheme } from '../../../src/theme/useTheme'
import { getCardDimensions, getWinRateCoinSubtitleStyles } from './FriendCard.styles'
import type { Friend } from '../../../src/friends/types/friends.types'
import type { PlayerRank } from '../../../src/economy/constants/playerRanks.constants'


interface FriendCardAcceptedProps {
  friend: Friend
  cardWidth: number
  /** Rank crest + level; null while unknown (specs/rank-on-social-screens AC-7/AC-14). */
  playerRank?: PlayerRank | null
  onRemoveFriend?: (friendId: string, friend?: Friend) => void
  onSelect?: () => void
  isSelected?: boolean
  /** When false, hide win rate subtitle (e.g. in invite selector). Default true. */
  showWinRate?: boolean
  /** When true, show current coin balance instead of win/loss stats. */
  showCoins?: boolean
  /** When true (e.g. invite modal), use smaller text so it fits in narrow cards. Default false. */
  compactText?: boolean
  /** When true (e.g. stake too high), card is non-interactive and should dim with parent. */
  disabled?: boolean
  testID?: string
}

export const FriendCardAccepted: React.FC<FriendCardAcceptedProps> = ({
  friend,
  cardWidth,
  playerRank = null,
  onRemoveFriend,
  onSelect,
  isSelected = false,
  showWinRate = true,
  showCoins = false,
  compactText = false,
  disabled = false,
  testID,
}) => {
  const router = useRouter()
  const theme = useTheme()

  const handleCardPress = () => {
    if (disabled) return
    if (onSelect) {
      onSelect()
    } else {
      router.push(`/app/friends/${friend.id}`)
    }
  }

  const handleRemove = (e: GestureResponderEvent) => {
    e.stopPropagation()
    onRemoveFriend?.(friend.id, friend)
  }

  const dimensions = getCardDimensions(cardWidth, compactText)
  const isMonkey = friend.id === MONKEY_USER_ID
  const friendDisplayName = getSafeDisplayName(friend.name, friend.email)
  const cardA11yLabel = onSelect
    ? `Select ${friendDisplayName}`
    : `View ${friendDisplayName}'s profile`

  const hasAnyChallenges =
    (friend.totalChallengesIncludingActive ?? friend.totalChallenges ?? 0) > 0
  const coinsDelta = friend.coinsDelta ?? 0
  const coinsDeltaLabel =
    coinsDelta > 0
      ? `+${coinsDelta.toLocaleString()}`
      : coinsDelta < 0
        ? coinsDelta.toLocaleString()
        : '0'

  const winRateCoinStyles = useMemo(
    () => getWinRateCoinSubtitleStyles(theme, dimensions),
    [theme, dimensions]
  )
  const winRateValueToneStyle =
    coinsDelta > 0
      ? winRateCoinStyles.valuePositive
      : coinsDelta < 0
        ? winRateCoinStyles.valueNegative
        : winRateCoinStyles.valueNeutral

  /** No shared games: show 0 + coin icon, not “No games yet”. Monkey: keep empty when no games. */
  const showWinRateCoinRow = showWinRate && (hasAnyChallenges || !isMonkey)
  const coinDeltaRow = (
    <View style={winRateCoinStyles.row}>
      <Text style={[winRateCoinStyles.value, winRateValueToneStyle]}>{coinsDeltaLabel}</Text>
      <Image
        source={IDLE_COIN_SOURCE}
        style={winRateCoinStyles.image}
        contentFit="contain"
        alt={WEB_IMAGE_ALT_DECORATIVE}
      />
    </View>
  )

  const subtitle = showCoins ? undefined : showWinRateCoinRow ? coinDeltaRow : undefined

  return (
    <BaseCard
      title={friendDisplayName}
      subtitle={subtitle}
      accessibilityLabel={cardA11yLabel}
      removeAccessibilityLabel={`Remove ${friendDisplayName}`}
      cardWidth={cardWidth}
      compact={compactText}
      avatarId={friend.avatarId}
      avatarConfig={friend.avatarConfig}
      avatarUrl={friend.avatar}
      avatarName={friend.name}
      showProfileBadge={friend.id !== MONKEY_USER_ID}
      playerRank={isMonkey ? null : playerRank}
      coinValue={showCoins ? (friend.coins ?? 0) : undefined}
      onRemove={
        onSelect === undefined &&
        onRemoveFriend &&
        friend.id !== MONKEY_USER_ID
          ? handleRemove
          : undefined
      }
      showSelectionIndicator={onSelect !== undefined && isSelected}
      disabled={disabled}
      onPress={handleCardPress}
      testID={testID || `friend-card-accepted-${friend.id}`}
    />
  )
}

